import { useMemo, useState } from 'react'
import { isAddress, type Address } from 'viem'
import { formatAmount, shortAddress } from '../lib/format'
import { isNative, type TokenMeta } from '../lib/tokens'
import type { BalanceMap } from '../hooks/useBalances'
import type { TokenListState } from '../hooks/useTokenList'

/**
 * Pick a token, or paste one.
 *
 * The list is the API's `GET /tokens` — every token in a pool the engine would route
 * through — with the base tokens standing in until it loads. Filtering matches on symbol,
 * name and address, and an address that matches nothing is offered for import rather than
 * reported as "no results". An import the API does not list is allowed, and says plainly
 * that it will almost certainly not route.
 */
export function TokenSelect({
  open,
  list,
  balances,
  exclude,
  onSelect,
  onClose,
}: {
  open: boolean
  list: TokenListState
  balances: BalanceMap
  exclude: Address | null
  onSelect: (token: TokenMeta) => void
  onClose: () => void
}) {
  const [search, setSearch] = useState('')
  const [importing, setImporting] = useState<TokenMeta | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase()
    return list.tokens.filter((token) => {
      if (token.address === exclude) return false
      if (needle.length === 0) return true
      return (
        token.symbol.toLowerCase().includes(needle) ||
        token.name.toLowerCase().includes(needle) ||
        token.address.toLowerCase().includes(needle)
      )
    })
  }, [list.tokens, search, exclude])

  async function lookup(value: string) {
    setError(null)
    setImporting(null)
    if (!isAddress(value.trim())) return
    // Already on the list: the address filter shows its row, and an import prompt beside it
    // would claim it was read from the contract rather than from the list.
    if (list.find(value.trim() as Address)) return
    setBusy(true)
    try {
      const token = await list.resolve(value.trim())
      setImporting(token)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'could not read that contract')
    } finally {
      setBusy(false)
    }
  }

  if (!open) return null

  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <div className="modal" onClick={(event) => event.stopPropagation()} role="dialog" aria-label="Select a token">
        <header className="modal-head">
          <h3>Select a token</h3>
          <button type="button" className="btn btn-ghost btn-small" onClick={onClose}>
            Close
          </button>
        </header>

        <input
          className="input"
          autoFocus
          placeholder="Search name, symbol, or paste an address"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value)
            void lookup(event.target.value)
          }}
        />

        <ListStatus list={list} />
        {busy && <p className="hint">Reading the contract…</p>}
        {error && <p className="hint hint-error">{error}</p>}

        {importing && (
          <div className="import-row">
            <div>
              <strong>{importing.symbol}</strong>
              <span className="muted"> {importing.name}</span>
              <div className="mono muted">{importing.address}</div>
              {list.status === 'live' && list.filter ? (
                <p className="hint hint-warn">
                  Not on the routable list: no {list.filter.protocols.join(' / ')} pool with at least{' '}
                  {formatUsd(list.filter.minPoolTvlUsd)} of TVL touches it, so a quote will almost certainly fail. Read
                  from the contract, not from the list.
                </p>
              ) : (
                <p className="hint">
                  Read from the contract, not from a list. Whether it is routable is answered by the first quote — the
                  indexer excludes tokens it could not prove clean.
                </p>
              )}
            </div>
            <button
              type="button"
              className="btn btn-primary btn-small"
              onClick={() => {
                list.add(importing)
                onSelect(importing)
                setImporting(null)
                setSearch('')
                onClose()
              }}
            >
              Import
            </button>
          </div>
        )}

        <ul className="token-list">
          {filtered.map((token) => {
            const balance = balances[token.address]
            return (
              <li key={token.address}>
                <button
                  type="button"
                  className="token-row"
                  onClick={() => {
                    onSelect(token)
                    setSearch('')
                    onClose()
                  }}
                >
                  <span className="token-row-main">
                    <span className="token-row-title">
                      <strong>{token.symbol}</strong>
                      {list.status === 'live' && !list.isListed(token.address) && (
                        <span className="tag" title="Imported by address; not on the API's routable list">
                          not listed
                        </span>
                      )}
                    </span>
                    <span className="muted">{token.name}</span>
                    {/* Two listed tokens answer to this symbol, so the address has to be on screen. */}
                    {token.symbolCollision && <span className="muted mono">{token.address}</span>}
                  </span>
                  <span className="token-row-side">
                    {balance !== undefined ? (
                      <span>{formatAmount(balance, token.decimals)}</span>
                    ) : isNative(token) ? (
                      <span className="muted">native</span>
                    ) : (
                      <span className="muted mono">{shortAddress(token.address)}</span>
                    )}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>

        {filtered.length === 0 && !importing && !busy && !error && (
          <p className="hint">No listed token matches. Paste its address to import it.</p>
        )}
      </div>
    </div>
  )
}

function ListStatus({ list }: { list: TokenListState }) {
  if (list.status === 'loading') return <p className="hint">Loading routable tokens…</p>
  if (list.status === 'unavailable') {
    return (
      <p className="hint hint-warn">
        Could not load the routable token list{list.listError ? ` (${list.listError.message})` : ''} — showing the base
        tokens only. Paste an address to use another.
      </p>
    )
  }
  return null
}

function formatUsd(value: number): string {
  return `$${value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
}
